/**
 * Odds Estimator Module
 * 
 * Provides functions to estimate combo odds when real market odds are unavailable.
 * Uses probabilistic models with correlation adjustments for accuracy.
 */

// ============================================================================
// Types
// ============================================================================

/**
 * Full odds row from the database
 */
export interface OddsRow {
  fixture_id: number;
  home_odds: number | null;
  draw_odds: number | null;
  away_odds: number | null;
  // Over/Under lines (bet_id=5)
  over_15_odds: number | null;
  under_15_odds: number | null;
  over_25_odds: number | null;
  under_25_odds: number | null;
  over_35_odds: number | null;
  under_35_odds: number | null;
  over_45_odds: number | null;
  under_45_odds: number | null;
  over_55_odds: number | null;
  under_55_odds: number | null;
  // BTTS (bet_id=8)
  btts_yes_odds: number | null;
  btts_no_odds: number | null;
  // Double Chance (bet_id=12)
  dc_1x_odds: number | null;
  dc_12_odds: number | null;
  dc_x2_odds: number | null;
  // Result/Total combos (bet_id=25)
  home_over_25_odds: number | null;
  home_under_25_odds: number | null;
  draw_over_25_odds: number | null;
  draw_under_25_odds: number | null;
  away_over_25_odds: number | null;
  away_under_25_odds: number | null;
  // First Half markets
  h1_over_05_odds: number | null;
  h1_under_05_odds: number | null;
  h1_over_15_odds: number | null;
  h1_under_15_odds: number | null;
  h1_home_odds: number | null;
  h1_draw_odds: number | null;
  h1_away_odds: number | null;
}

/**
 * Subset of OddsRow used for estimation calculations
 */
export type OddsData = Pick<OddsRow,
  | 'home_odds' | 'draw_odds' | 'away_odds'
  | 'over_15_odds' | 'under_15_odds'
  | 'over_25_odds' | 'under_25_odds'
  | 'over_35_odds' | 'under_35_odds'
  | 'over_45_odds' | 'under_45_odds'
  | 'over_55_odds' | 'under_55_odds'
  | 'btts_yes_odds' | 'btts_no_odds'
  | 'dc_1x_odds' | 'dc_12_odds' | 'dc_x2_odds'
>;

export interface ResolvedOdds {
  value: number;
  source: 'REAL' | 'EST' | 'INF';
  confidence: 'high' | 'medium' | 'low';
}

export interface ParsedPickId {
  market: string;
  legs: string[];
}

// ============================================================================
// Constants
// ============================================================================

const VIG_FACTOR = 0.05; // Typical 5% bookmaker margin
const MIN_PROB = 0.0001; // Prevents odds > 10000
const MAX_PROB = 0.99;   // Prevents odds < 1.01
const FALLBACK_ODD = 1.01;

// DC_TOTAL correlation factors (calibrated against Betano odds)
const DC_CORR_BOOST_UNDER = 0.08;  // Boost for defensive combos (1X+Under, X2+Under)
const DC_CORR_BOOST_OVER = 0.06;   // Boost for 12+Over
const DC_CORR_PENALTY = 0.02;      // Penalty for conflicting combos

// ============================================================================
// Normalization Functions
// ============================================================================

/**
 * Remove bookmaker vig from 1X2 odds to get fair probabilities
 */
export function normalize1X2(home: number, draw: number, away: number): {
  pHome: number;
  pDraw: number;
  pAway: number;
} {
  const impliedHome = 1 / home;
  const impliedDraw = 1 / draw;
  const impliedAway = 1 / away;
  const total = impliedHome + impliedDraw + impliedAway;

  return {
    pHome: impliedHome / total,
    pDraw: impliedDraw / total,
    pAway: impliedAway / total,
  };
}

/**
 * Normalize a pair of Over/Under odds to get fair probabilities
 * Only uses REAL odds - never infers missing values
 */
export function normalizeTotalPair(
  overOdds: number | null,
  underOdds: number | null
): { pOver: number; pUnder: number } | null {
  if (overOdds === null || underOdds === null) return null;
  if (overOdds <= 1 || underOdds <= 1) return null;

  const impliedOver = 1 / overOdds;
  const impliedUnder = 1 / underOdds;
  const total = impliedOver + impliedUnder;

  return {
    pOver: impliedOver / total,
    pUnder: impliedUnder / total,
  };
}

// ============================================================================
// Correlation Functions
// ============================================================================

/**
 * Get correlation boost based on match favoritism
 * Stronger favorites have higher correlation with Over markets
 */
export function getCorrelationBoost(homeOdds: number | null): number {
  if (homeOdds === null) return 0.015; // Default small boost

  if (homeOdds <= 1.60) return 0.08;   // Heavy favorite
  if (homeOdds <= 2.10) return 0.045;  // Moderate favorite
  return 0.015;                         // No clear favorite
}

// ============================================================================
// Pick ID Parsing
// ============================================================================

/**
 * Parse a structured pick ID into its components
 * 
 * Examples:
 *   "ft_1x2:home" -> { market: "ft_1x2", legs: ["home"] }
 *   "result_total:home:over:2.5" -> { market: "result_total", legs: ["home", "over", "2.5"] }
 *   "total:under:3.5" -> { market: "total", legs: ["under", "3.5"] }
 */
export function parsePickId(pickId: string | undefined | null): ParsedPickId | null {
  if (!pickId || typeof pickId !== 'string') return null;

  const parts = pickId.toLowerCase().split(':');
  if (parts.length < 2) return null;

  return {
    market: parts[0],
    legs: parts.slice(1),
  };
}

/**
 * Parse combo legs from a pick label when pickId is missing
 * e.g., "12 + Over 1.5" → ["12", "over", "1.5"]
 *       "1X + Under 2.5" → ["1x", "under", "2.5"]
 */
export function parseLegsFromLabel(label: string): string[] | null {
  if (!label) return null;
  
  const legs: string[] = [];
  const upper = label.toUpperCase();
  
  // Detect DC legs: 12, 1X, X2
  if (/\b12\b/.test(upper)) {
    legs.push('12');
  } else if (/\b1X\b/.test(upper)) {
    legs.push('1x');
  } else if (/\bX2\b/.test(upper)) {
    legs.push('x2');
  }
  
  // Detect Over/Under with line
  const totalMatch = upper.match(/\b(OVER|UNDER)\s*(\d+\.?\d*)/);
  if (totalMatch) {
    legs.push(totalMatch[1].toLowerCase());
    legs.push(totalMatch[2]);
  }
  
  // Detect result legs (Home/Away/Draw) - for Result+Total combos
  if (/\bHOME\s*(WIN)?\b/.test(upper) && !/\b1X\b/.test(upper)) {
    legs.push('home');
  } else if (/\bAWAY\s*(WIN)?\b/.test(upper) && !/\bX2\b/.test(upper)) {
    legs.push('away');
  } else if (/\bDRAW\b/.test(upper) && !/\b1X\b/.test(upper) && !/\bX2\b/.test(upper)) {
    legs.push('draw');
  }
  
  return legs.length >= 2 ? legs : null;
}

// ============================================================================
// Main Estimation Function
// ============================================================================

/**
 * Estimate combo odds by multiplying normalized probabilities
 * with correlation adjustments
 */
export function estimateComboOdds(
  legs: string[],
  oddsData: OddsData
): ResolvedOdds | null {
  // Need at least 1X2 odds for any estimation
  if (!oddsData.home_odds || !oddsData.draw_odds || !oddsData.away_odds) {
    return null;
  }

  const { pHome, pDraw, pAway } = normalize1X2(
    oddsData.home_odds,
    oddsData.draw_odds,
    oddsData.away_odds
  );

  let pJoint = 1;
  let hasResultLeg = false;
  let hasTotalLeg = false;
  let hasDCLeg = false;
  let totalLine: string | null = null;
  let totalDirection: 'over' | 'under' | null = null;
  let resultLeg: 'home' | 'draw' | 'away' | null = null;
  let dcLeg: '1x' | 'x2' | '12' | null = null;

  // Parse each leg
  for (const leg of legs) {
    const legLower = leg.toLowerCase();

    // Result legs
    if (legLower === 'home' || legLower === '1') {
      pJoint *= pHome;
      hasResultLeg = true;
      resultLeg = 'home';
    } else if (legLower === 'draw' || legLower === 'x') {
      pJoint *= pDraw;
      hasResultLeg = true;
      resultLeg = 'draw';
    } else if (legLower === 'away' || legLower === '2') {
      pJoint *= pAway;
      hasResultLeg = true;
      resultLeg = 'away';
    }
    // Double Chance legs
    else if (legLower === '1x' || legLower === 'home_draw') {
      dcLeg = '1x';
    } else if (legLower === 'x2' || legLower === 'draw_away') {
      dcLeg = 'x2';
    } else if (legLower === '12' || legLower === 'home_away') {
      dcLeg = '12';
    }
    // Over legs
    else if (legLower === 'over') {
      totalDirection = 'over';
    } else if (legLower === 'under') {
      totalDirection = 'under';
    }
    // Line values
    else if (/^\d+\.?\d*$/.test(legLower)) {
      totalLine = legLower;
    }
  }

  // Apply total leg probability
  if (totalDirection && totalLine) {
    hasTotalLeg = true;
    const lineKey = totalLine.replace('.', '');
    const overKey = `over_${lineKey}_odds` as keyof OddsData;
    const underKey = `under_${lineKey}_odds` as keyof OddsData;

    const overOdds = oddsData[overKey] as number | null;
    const underOdds = oddsData[underKey] as number | null;

    const normalized = normalizeTotalPair(overOdds, underOdds);
    if (normalized) {
      const pTotal = totalDirection === 'over' ? normalized.pOver : normalized.pUnder;
      pJoint *= pTotal;
    } else {
      // Can't estimate without real total odds
      return null;
    }
  }

  // Apply DC leg probability from normalized 1X2
  if (dcLeg) {
    hasDCLeg = true;
    let pDC: number;
    
    // Calculate DC probability from already normalized 1X2 probabilities
    if (dcLeg === '1x') {
      pDC = pHome + pDraw;       // P(Home OR Draw)
    } else if (dcLeg === 'x2') {
      pDC = pDraw + pAway;       // P(Draw OR Away)
    } else { // '12'
      pDC = pHome + pAway;       // P(Home OR Away)
    }
    
    pJoint *= pDC;
  }

  // Must have at least one identifiable leg
  if (!hasResultLeg && !hasTotalLeg && !hasDCLeg) {
    return null;
  }

  // Apply correlation adjustment for combos
  if (hasResultLeg && hasTotalLeg) {
    const boost = getCorrelationBoost(oddsData.home_odds);

    // Positive correlation: Home + Over, Away + Under
    // Negative correlation: Draw + Over, Home + Under
    if (resultLeg === 'home' && totalDirection === 'over') {
      pJoint *= (1 + boost);
    } else if (resultLeg === 'away' && totalDirection === 'under') {
      pJoint *= (1 + boost * 0.7);
    } else if (resultLeg === 'draw' && totalDirection === 'over') {
      pJoint *= (1 - boost * 0.5);
    } else if (resultLeg === 'home' && totalDirection === 'under') {
      pJoint *= (1 - boost * 0.3);
    }
  }

  // Apply correlation adjustment for DC_TOTAL combos
  if (hasDCLeg && hasTotalLeg) {
    // Dynamic correlation multiplier based on total line
    // Lower lines (1.5) have stronger correlation, higher lines (3.5) weaker
    let lineMultiplier = 1.0;
    if (totalLine === '1.5' || totalLine === '15') {
      lineMultiplier = 1.15;  // Stronger correlation for low lines
    } else if (totalLine === '3.5' || totalLine === '35') {
      lineMultiplier = 0.85;  // Weaker correlation for high lines
    }
    // 2.5 keeps default multiplier of 1.0

    // Positive correlation: defensive DC combos with Under
    // 1X + Under: If home doesn't lose, game tends to be tight
    if (dcLeg === '1x' && totalDirection === 'under') {
      pJoint *= (1 + DC_CORR_BOOST_UNDER * lineMultiplier);
    }
    // X2 + Under: If home doesn't win, fewer goals expected
    else if (dcLeg === 'x2' && totalDirection === 'under') {
      pJoint *= (1 + DC_CORR_BOOST_UNDER * lineMultiplier);
    }
    // 12 + Over: If someone wins decisively, more goals likely
    else if (dcLeg === '12' && totalDirection === 'over') {
      pJoint *= (1 + DC_CORR_BOOST_OVER * lineMultiplier);
    }
    // Negative correlation: conflicting combos
    // 1X + Over: Protection vs attack conflict
    else if (dcLeg === '1x' && totalDirection === 'over') {
      pJoint *= (1 - DC_CORR_PENALTY * lineMultiplier);
    }
    // X2 + Over: Protection vs attack conflict  
    else if (dcLeg === 'x2' && totalDirection === 'over') {
      pJoint *= (1 - DC_CORR_PENALTY * lineMultiplier);
    }
    // 12 + Under: Decisive win vs low scoring conflict
    else if (dcLeg === '12' && totalDirection === 'under') {
      pJoint *= (1 - DC_CORR_PENALTY * lineMultiplier);
    }
  }

  // Bilateral clamp to prevent extreme values
  const pJointClamped = Math.max(MIN_PROB, Math.min(pJoint, MAX_PROB));

  // Convert probability to decimal odds with margin
  const fairOdds = 1 / pJointClamped;
  const marginedOdds = fairOdds * (1 - VIG_FACTOR);

  // Ensure minimum valid odd
  const finalOdds = Math.max(1.01, marginedOdds);

  return {
    value: Math.round(finalOdds * 100) / 100, // Round to 2 decimals
    source: 'EST',
    confidence: pJointClamped > 0.3 ? 'high' : pJointClamped > 0.1 ? 'medium' : 'low',
  };
}

// ============================================================================
// Resolution Helpers
// ============================================================================

/**
 * Get the real odds column name for a simple market pick
 */
export function getRealOddsKey(
  market: string,
  selection: string
): keyof OddsRow | null {
  const m = market.toLowerCase();
  const s = selection.toLowerCase();

  // 1X2 / Match Winner
  if (m === 'ft_1x2' || m === '1x2' || m === 'match_winner') {
    if (s === 'home' || s === '1') return 'home_odds';
    if (s === 'draw' || s === 'x') return 'draw_odds';
    if (s === 'away' || s === '2') return 'away_odds';
  }

  // Over/Under
  if (m === 'total' || m === 'over_under' || m === 'goals') {
    const match = s.match(/(over|under)[_\s]?(\d+\.?\d*)/i);
    if (match) {
      const dir = match[1].toLowerCase();
      const line = match[2].replace('.', '');
      return `${dir}_${line}_odds` as keyof OddsRow;
    }
  }

  // BTTS
  if (m === 'btts' || m === 'both_teams_to_score') {
    if (s === 'yes') return 'btts_yes_odds';
    if (s === 'no') return 'btts_no_odds';
  }

  // Double Chance
  if (m === 'double_chance' || m === 'dc') {
    if (s === '1x' || s === 'home_draw') return 'dc_1x_odds';
    if (s === '12' || s === 'home_away') return 'dc_12_odds';
    if (s === 'x2' || s === 'draw_away') return 'dc_x2_odds';
  }

  // Result + Total combo (bet_id=25)
  if (m === 'result_total') {
    // Format: "home:over:2.5" or "draw:under:2.5"
    const parts = s.split(':');
    if (parts.length >= 3) {
      const result = parts[0];
      const direction = parts[1];
      const line = parts[2].replace('.', '');
      
      if (result === 'home' && direction === 'over' && line === '25') return 'home_over_25_odds';
      if (result === 'home' && direction === 'under' && line === '25') return 'home_under_25_odds';
      if (result === 'draw' && direction === 'over' && line === '25') return 'draw_over_25_odds';
      if (result === 'draw' && direction === 'under' && line === '25') return 'draw_under_25_odds';
      if (result === 'away' && direction === 'over' && line === '25') return 'away_over_25_odds';
      if (result === 'away' && direction === 'under' && line === '25') return 'away_under_25_odds';
    }
  }

  // =====================================================
  // NEW CATALOG MARKETS - H1_GOAL, H2_GOAL, H1_TOTAL, etc.
  // =====================================================

  // H1 Goal (Goal in First Half) - uses H1 Over 0.5 as proxy
  if (m === 'h1_goal') {
    if (s === 'yes') return 'h1_over_05_odds';
    if (s === 'no') return 'h1_under_05_odds';
  }

  // H1 Total (1st Half Over/Under)
  if (m === 'h1_total' || m === '1h_total') {
    const match = s.match(/(over|under)[_\s]?(\d+\.?\d*)/i);
    if (match) {
      const dir = match[1].toLowerCase();
      const line = match[2].replace('.', '');
      const key = `h1_${dir}_${line}_odds`;
      // Only return if it's a known H1 key
      if (key === 'h1_over_05_odds' || key === 'h1_under_05_odds' ||
          key === 'h1_over_15_odds' || key === 'h1_under_15_odds') {
        return key as keyof OddsRow;
      }
    }
  }

  // H1 1X2 (First Half Result)
  if (m === '1h' || m === 'h1') {
    if (s === 'home' || s === '1') return 'h1_home_odds';
    if (s === 'draw' || s === 'x') return 'h1_draw_odds';
    if (s === 'away' || s === '2') return 'h1_away_odds';
  }

  return null;
}

/**
 * Resolve odds for a pick - tries REAL first, then falls back to EST
 */
export function resolveOddsForPick(
  pickId: string | undefined | null,
  n8nOdds: number | null,
  oddsData: OddsRow | null,
  label?: string
): ResolvedOdds {
  const parsed = parsePickId(pickId);

  // Try to get REAL odds from database
  if (parsed && oddsData) {
    // For result_total combos (bet_id=25), check direct column first
    if (parsed.market === 'result_total' && parsed.legs.length >= 3) {
      const key = getRealOddsKey('result_total', parsed.legs.join(':'));
      if (key && oddsData[key] !== null && oddsData[key] !== undefined) {
        const value = oddsData[key] as number;
        if (value > 1) {
          return { value, source: 'REAL', confidence: 'high' };
        }
      }
    }

    // For simple markets (1 leg)
    if (parsed.legs.length === 1) {
      const key = getRealOddsKey(parsed.market, parsed.legs[0]);
      if (key && oddsData[key] !== null && oddsData[key] !== undefined) {
        const value = oddsData[key] as number;
        if (value > 1) {
          return { value, source: 'REAL', confidence: 'high' };
        }
      }
    }

    // For total market with 2 legs (e.g., "total:under:4.5")
    if (parsed.market === 'total' && parsed.legs.length === 2) {
      const direction = parsed.legs[0].toLowerCase(); // "under" or "over"
      const line = parsed.legs[1];                    // "4.5"
      const lineKey = line.replace('.', '');          // "45"
      const key = `${direction}_${lineKey}_odds` as keyof OddsRow;
      
      if (oddsData[key] !== null && oddsData[key] !== undefined) {
        const value = oddsData[key] as number;
        if (value > 1) {
          return { value, source: 'REAL', confidence: 'high' };
        }
      }
    }

    // For dc market with 1 leg (e.g., "dc:1X")
    if (parsed.market === 'dc' && parsed.legs.length === 1) {
      const sel = parsed.legs[0].toLowerCase();
      let key: keyof OddsRow | null = null;
      
      if (sel === '1x' || sel === 'home_draw') key = 'dc_1x_odds';
      else if (sel === '12' || sel === 'home_away') key = 'dc_12_odds';
      else if (sel === 'x2' || sel === 'draw_away') key = 'dc_x2_odds';
      
      if (key && oddsData[key] !== null && oddsData[key] !== undefined) {
        const value = oddsData[key] as number;
        if (value > 1) {
          return { value, source: 'REAL', confidence: 'high' };
        }
      }
    }

    // For btts market with 1 leg (e.g., "btts:yes")
    if (parsed.market === 'btts' && parsed.legs.length === 1) {
      const sel = parsed.legs[0].toLowerCase();
      const key = sel === 'yes' ? 'btts_yes_odds' : sel === 'no' ? 'btts_no_odds' : null;
      
      if (key && oddsData[key] !== null && oddsData[key] !== undefined) {
        const value = oddsData[key] as number;
        if (value > 1) {
          return { value, source: 'REAL', confidence: 'high' };
        }
      }
    }
  }

  // Try N8N/LLM odds
  if (n8nOdds !== null && n8nOdds > 1) {
    return { value: n8nOdds, source: 'REAL', confidence: 'medium' };
  }

  // Try estimation for combos
  if (parsed && parsed.legs.length > 1 && oddsData) {
    const estimated = estimateComboOdds(parsed.legs, oddsData);
    if (estimated) {
      return estimated;
    }
  }

  // Fallback: Try estimation from label when pickId is missing
  if (!parsed && label && oddsData) {
    const legsFromLabel = parseLegsFromLabel(label);
    if (legsFromLabel && legsFromLabel.length >= 2) {
      const estimated = estimateComboOdds(legsFromLabel, oddsData);
      if (estimated) {
        return estimated;
      }
    }
  }

  // Fallback
  return { value: FALLBACK_ODD, source: 'EST', confidence: 'low' };
}
